import {useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";


function MiRNAAutocomplete({label, defaultValue, onChange}) {

    // Normalisation de l'entrée utilisateur selon ces règles :
    // - trim
    // - forcer miR (ou let) en début de chaîne
    // - forcer le reste en minuscules
    // - si entrée vide, retourner chaîne vide
    // Exemples:
    //   "mir-21-5p " → "miR-21-5p"
    //   "let-7a" -> "let-7a"
    const normalizeInput = (val) => {
        if (!val) return "";
        let s = val.trim();

        if (/^mir/i.test(s)) {
            s = "miR" + s.slice(3);
        }
        if (/^mir\d+/i.test(s)) {
            s = "miR-" + s.slice(3);
        }
        if (/^let/i.test(s)) {
            s = "let" + s.slice(3);
        }
        if (/^let\d+/i.test(s)) {
            s = "let-" + s.slice(3);
        }
        return s;
    };

    const [input, setInput] = useState(normalizeInput(defaultValue ?? ""));
    const [open, setOpen] = useState(false);
    const [hoverIdx, setHoverIdx] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState(null); // forme originale du JSON
    const [interacted, setInteracted] = useState(false);
    const [valid, setValid] = useState(false);
    const debounceRef = useRef();
    const lastCommittedRef = useRef(normalizeInput(defaultValue ?? ""));

    const DATA_URL = `${process.env.PUBLIC_URL || ""}/data/mirnas.json`;

    // Chargement lazy
    useEffect(() => {
        if (items !== null || input.trim().length < 2) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const res = await fetch(DATA_URL, {cache: "no-store"});
                const ct = res.headers.get("content-type") || "";
                if (!res.ok || !ct.includes("application/json")) { if (!cancelled) setItems([]); return; }
                const arr = await res.json();
                const cleaned = Array.isArray(arr)
                    ? Array.from(new Set(arr.map(String)))
                        .map(s => s.replace(/^.*[\\/]/, ""))
                        .map(s => s.replace(/\.[^.]+$/, ""))
                        // conserver le casing d'origine (supposé correct: miR-34a-5p)
                        .sort((a,b) => a.localeCompare(b))
                    : [];
                if (!cancelled) setItems(cleaned);
            } catch {
                if (!cancelled) setItems([]);
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [input, items, DATA_URL]);

    // Debounce
    const [debounced, setDebounced] = useState(input);
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebounced(input), 150);
        return () => clearTimeout(debounceRef.current);
    }, [input]);

    // Matches (case-insensitive, puis tri similaire précédent)
    const matches = useMemo(() => {
        const list = items || [];
        const qRaw = debounced.trim();
        if (!qRaw || list.length === 0) return [];
        // Normaliser la requête: appliquer nos règles miR + reste en minuscules
        const qNorm = normalizeInput(qRaw);
        const qLower = qNorm.toLowerCase();
        const hyphenPrefixLower = (qNorm + '-').toLowerCase();
        const strict = [], prefix = [], infix = [];
        let scanned = 0, SCAN_LIMIT = 2000, COLLECT_LIMIT = 300;
        for (const s of list) {
            const low = s.toLowerCase();
            if (low.startsWith(hyphenPrefixLower)) strict.push(s);
            else if (low.startsWith(qLower)) prefix.push(s);
            else if (low.includes(qLower)) infix.push(s);
            scanned++;
            if (scanned >= SCAN_LIMIT && (strict.length + prefix.length + infix.length) >= COLLECT_LIMIT) break;
        }
        const ordered = strict.length ? strict : [...prefix, ...infix];
        return ordered.slice(0, 500);
    }, [debounced, items]);

    const anchoredSome = useMemo(() => {
        const q = normalizeInput(debounced.trim()).toLowerCase();
        if (!q || matches.length === 0) return false;
        return matches.some(m => m.toLowerCase().startsWith(q + '-'));
    }, [debounced, matches]);

    useEffect(() => {
        if (!items || !input.trim()) { setValid(false); return; }
        const targetLow = normalizeInput(input).toLowerCase();
        setValid(items.some(it => it.toLowerCase() === targetLow));
    }, [items, input]);

    // Ouverture menu
    useEffect(() => {
        if (!interacted) { setOpen(false); setHoverIdx(-1); return; }
        const q = debounced.trim();
        const hasInput = q.length >= 2;
        const hasMatches = matches.length > 0;
        const qLow = normalizeInput(q).toLowerCase();
        const identicalSingle = matches.length === 1 && matches[0].toLowerCase() === qLow;
        const smallEnough = matches.length <= 500;
        const shouldOpen = hasInput && hasMatches && !identicalSingle && (smallEnough || anchoredSome);
        setOpen(shouldOpen);
        setHoverIdx(-1);
    }, [interacted, debounced, matches, anchoredSome]);

    const commit = (val) => {
        const raw = (val ?? input).trim();
        lastCommittedRef.current = raw;
        onChange?.(raw);
    };

    const pick = (s) => { setInput(s); setInteracted(true); commit(s); setOpen(false); };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (open && matches.length && hoverIdx >= 0) { e.preventDefault(); pick(matches[hoverIdx]); return; }
            e.preventDefault(); commit(); setOpen(false);
        } else if (e.key === 'ArrowDown') {
            if (!open && matches.length) setOpen(true);
            e.preventDefault(); setHoverIdx(i => (i + 1) % Math.max(matches.length, 1));
        } else if (e.key === 'ArrowUp') {
            if (!open && matches.length) setOpen(true);
            e.preventDefault(); setHoverIdx(i => (i - 1 + Math.max(matches.length, 1)) % Math.max(matches.length, 1));
        } else if (e.key === 'Escape') { setOpen(false); }
    };

    return (
        <Box sx={{position: 'relative', maxWidth: 540, mb: 2}}>
            <TextField
                label={label}
                value={input}
                onChange={e => { const norm = normalizeInput(e.target.value); setInput(norm); setInteracted(true); }}
                onFocus={() => { if (interacted && matches.length) setOpen(true); }}
                onBlur={() => { commit(); setTimeout(() => setOpen(false), 120); }}
                onKeyDown={onKeyDown}
                fullWidth
                size='small'
                variant='outlined'
                error={!!input && !valid}
                helperText={!!input && !valid ? 'Select a valid miRNA from list' : ''}
                required
            />
            {loading && (
                <Box sx={{fontSize: 12, mt: 1, opacity: 0.7, display: 'flex', alignItems: 'center'}}>
                    <CircularProgress size={16} sx={{mr: 1}}/> Loading...
                </Box>
            )}
            {open && matches.length > 0 && (
                <Paper sx={{position: 'absolute', zIndex: 10, mt: 1, width: '100%', maxHeight: 240, overflowY: 'auto'}}>
                    <List>
                        {matches.map((m, idx) => (
                            <ListItem disablePadding key={m}>
                                <ListItemButton
                                    selected={idx === hoverIdx}
                                    onMouseDown={e => { e.preventDefault(); pick(m); }}
                                    onMouseEnter={() => setHoverIdx(idx)}
                                >
                                    <ListItemText primary={m}/>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );
}

export default MiRNAAutocomplete;
